import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Check, Mail, Calendar, Users, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';

const TourBookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      handlePaymentSuccess();
    } else {
      setError('No session ID provided');
      setLoading(false);
    }
  }, [sessionId]);

  const handlePaymentSuccess = async () => {
    try {
      // Direct booking completion - no polling needed
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tours/checkout/complete/${encodeURIComponent(sessionId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok) {
        if (result.status === 'success') {
          // Booking completed successfully
          setBooking({
            reference: result.booking_reference,
            message: result.message,
            customerName: result.customer_name,
            tourDate: result.tour_date,
            tourTime: result.tour_time,
            alreadyProcessed: result.already_processed || false
          });
        } else {
          // Booking failed
          setError(result.message || 'Failed to complete booking');
        }
      } else {
        setError(result.message || 'Failed to complete booking');
      }
    } catch (error) {
      console.error('Error completing booking:', error);
      setError('Failed to complete booking. Please contact support with your payment confirmation.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4 mx-auto"></div>
          <p className="text-gray-600 mb-2">Completing your booking...</p>
          <p className="text-sm text-gray-500">
            Payment successful! Creating your tour booking and sending confirmation email.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <CardTitle className="text-red-600">Booking Error</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert className="border-red-200 bg-red-50 mb-4">
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
            <div className="text-center">
              <Button asChild variant="outline">
                <Link to="/book-tour">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Try Again
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* Success Hero Section */}
      <div className="relative bg-gradient-to-r from-green-600 to-blue-500 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-6">
            <Check className="w-10 h-10" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            🎉 Booking Confirmed!
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            Your underground mine tour has been successfully booked
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Booking Confirmation */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-green-600" />
                Booking Confirmation
              </CardTitle>
              <CardDescription>
                Your tour tickets have been confirmed and sent to your email
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="font-semibold text-green-800 mb-2">
                    Booking Reference Number
                  </div>
                  <div className="text-2xl font-bold text-green-900">
                    {booking?.reference}
                  </div>
                </div>

                <Alert className="border-blue-200 bg-blue-50">
                  <Mail className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    <strong>Check your email!</strong> Your PDF tickets and booking confirmation 
                    have been sent to your email address. Please bring your tickets (printed or digital) on tour day.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2 text-sm">
                  <h4 className="font-semibold">What happens next:</h4>
                  <ul className="space-y-1 text-gray-600">
                    <li>• PDF tickets sent to your email immediately</li>
                    <li>• Admin notification sent for tour preparation</li>
                    <li>• Arrive 15 minutes before your tour time</li>
                    <li>• Present your tickets at the entrance</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Important Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                Important Information
              </CardTitle>
              <CardDescription>
                Please read before your tour
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <h4 className="font-semibold text-purple-800 mb-2">📍 Location</h4>
                  <p className="text-purple-700 text-sm">
                    Umoona Opal Mine & Museum<br/>
                    Hutchison Street, Coober Pedy SA 5723
                  </p>
                </div>

                <div className="space-y-3 text-sm">
                  <div>
                    <h4 className="font-semibold text-gray-900">⏰ Arrival</h4>
                    <p className="text-gray-600">Please arrive 15 minutes before your scheduled tour time</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900">👟 What to Wear</h4>
                    <p className="text-gray-600">Comfortable walking shoes and clothing. Underground temperature is constant 22°C (72°F)</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900">📱 Contact</h4>
                    <p className="text-gray-600">
                      Phone: (08) 8672 5288<br/>
                      Email: info@umoonaopalmine.com.au
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="text-center mt-12 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-gradient-to-r from-green-600 to-blue-500 hover:from-green-700 hover:to-blue-600">
              <Link to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/book-tour">
                <Calendar className="w-4 h-4 mr-2" />
                Book Another Tour
              </Link>
            </Button>
          </div>

          <p className="text-gray-600 text-sm mt-8">
            Thank you for choosing Umoona Opal Mine for your underground adventure!
          </p>
        </div>
      </div>
    </div>
  );
};

export default TourBookingSuccessPage;